/**
 * Backend API Response Contracts
 * 
 * These types define the ACTUAL format returned by the backend APIs
 * This prevents runtime surprises and enables compile-time validation
 */

import { z } from 'zod';

// =============================================================================
// Base Backend Response Format
// =============================================================================

/**
 * Standard backend success response wrapper
 */
export interface BackendSuccessResponse<T> {
  success: true;
  data: T;
}

/**
 * Standard backend error response
 */
export interface BackendErrorResponse {
  success: false;
  error: {
    message: string;
    code?: string;
    details?: Record<string, any>;
  };
}

/**
 * Union type for all backend responses
 */
export type BackendResponse<T> = BackendSuccessResponse<T> | BackendErrorResponse;

// =============================================================================
// GTD-Specific Backend Response Types
// =============================================================================

/**
 * Raw inbox item as returned by backend
 */
export interface BackendInboxItem {
  id: string;
  content: string;
  text?: string;
  title?: string;
  status: 'unprocessed' | 'processed' | 'clarified';
  capturedAt: string;
  createdAt: string;
  capturedBy: string;
  capturedByPerson?: string | null;
  clarification?: string | null;
  description?: string | null;
  isActionable?: boolean | null;
  tags: string[];
}

/**
 * Backend inbox list response data
 */
export interface BackendInboxListData {
  items: BackendInboxItem[];
  total: number;
}

/**
 * Complete backend inbox list response
 */
export type BackendInboxListResponse = BackendResponse<BackendInboxListData>;

/**
 * Raw project as returned by backend
 */
export interface BackendProject {
  id: string;
  name?: string;
  title?: string;
  description: string;
  status: 'active' | 'completed' | 'someday' | 'cancelled';
  createdAt: string;
  ownerId?: string;
  createdBy?: string;
  priority?: 'low' | 'medium' | 'high';
  progress: {
    totalActions: number;
    completedActions: number;
    completionPercentage: number;
  };
}

/**
 * Backend projects list response data
 */
export interface BackendProjectsListData {
  projects: BackendProject[];  // Note: 'projects' not 'items'!
  total?: number;
}

/**
 * Complete backend projects list response
 */
export type BackendProjectsListResponse = BackendResponse<BackendProjectsListData>;

/**
 * Backend inbox item creation request
 */
export interface BackendCreateInboxItemRequest {
  content: string;
  capturedBy: string;
}

/**
 * Backend inbox item creation response data
 */
export interface BackendCreateInboxItemData {
  item: BackendInboxItem;
}

/**
 * Complete backend inbox creation response
 */
export type BackendCreateInboxItemResponse = BackendResponse<BackendCreateInboxItemData>;

// =============================================================================
// Zod Schemas for Runtime Validation
// =============================================================================

export const BackendInboxItemSchema = z.object({
  id: z.string(),
  content: z.string(),
  text: z.string().optional(),
  title: z.string().optional(),
  status: z.enum(['unprocessed', 'processed', 'clarified']),
  capturedAt: z.string(),
  createdAt: z.string(),
  capturedBy: z.string(),
  capturedByPerson: z.string().nullable().optional(),
  clarification: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  isActionable: z.boolean().nullable().optional(),
  tags: z.array(z.string()),
});

export const BackendProjectSchema = z.object({
  id: z.string(),
  name: z.string().optional(),
  title: z.string().optional(), 
  description: z.string(),
  status: z.enum(['active', 'completed', 'someday', 'cancelled']),
  createdAt: z.string(),
  ownerId: z.string().optional(),
  createdBy: z.string().optional(),
  priority: z.enum(['low', 'medium', 'high']).optional(),
  progress: z.object({
    totalActions: z.number(),
    completedActions: z.number(),
    completionPercentage: z.number(),
  }),
});

export const BackendSuccessResponseSchema = <T extends z.ZodType>(dataSchema: T) =>
  z.object({
    success: z.literal(true),
    data: dataSchema,
  });

export const BackendErrorResponseSchema = z.object({
  success: z.literal(false),
  error: z.object({
    message: z.string(),
    code: z.string().optional(),
    details: z.record(z.any()).optional(),
  }),
});

export const BackendInboxListResponseSchema = z.union([
  BackendSuccessResponseSchema(z.object({
    items: z.array(BackendInboxItemSchema),
    total: z.number(),
  })),
  BackendErrorResponseSchema,
]);

export const BackendProjectsListResponseSchema = z.union([
  BackendSuccessResponseSchema(z.object({
    projects: z.array(BackendProjectSchema),
    total: z.number().optional(),
  })),
  BackendErrorResponseSchema,
]);

// =============================================================================
// Type Guards for Runtime Checking
// =============================================================================

export function isBackendSuccessResponse<T>(
  response: BackendResponse<T>
): response is BackendSuccessResponse<T> {
  return response.success === true;
}

export function isBackendErrorResponse<T>(
  response: BackendResponse<T>
): response is BackendErrorResponse {
  return response.success === false;
}

// =============================================================================
// Error Classes
// =============================================================================

export class BackendApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly details?: Record<string, any>
  ) {
    super(message);
    this.name = 'BackendApiError';
  }
  
  static fromBackendError(errorResponse: BackendErrorResponse): BackendApiError {
    return new BackendApiError(
      errorResponse.error.code || 'UNKNOWN_ERROR',
      errorResponse.error.message,
      errorResponse.error.details
    );
  }
}

export class ApiValidationError extends Error {
  constructor(message: string, public readonly validationErrors: any) {
    super(message);
    this.name = 'ApiValidationError';
  }
}